/**
 * Server-side PostHog analytics
 * @package @app/api
 *
 * Serverless-safe: captures flush immediately because Vercel Functions are
 * killed as soon as the response is sent. Without flushing per call, queued
 * events would be lost.
 *
 * The same `phc_…` project key is used for client and server (VITE_POSTHOG_KEY
 * is exposed to the browser; POSTHOG_KEY is the server-only alias). Falls back
 * to VITE_POSTHOG_KEY so a single env var configures both sides.
 *
 * IMPORTANT: posthog-node is loaded via dynamic import inside a try/catch so
 * a missing/broken package can never fail at ESM link time. A top-level
 * `import { PostHog } from 'posthog-node'` here previously took down the SSR
 * router chunk in production when the lambda's node_modules didn't include
 * posthog-node (Vite's ssr.noExternal did not actually inline it). Module
 * link failures bypass runtime try/catch — the import MUST stay dynamic.
 */

import type { PostHog } from 'posthog-node';

let client: PostHog | null | undefined;
let clientInitFailed = false;

async function getClient(): Promise<PostHog | null> {
  if (client !== undefined) return client;
  if (clientInitFailed) return null;
  const key = process.env.POSTHOG_KEY ?? process.env.VITE_POSTHOG_KEY;
  if (!key) {
    client = null;
    return null;
  }
  try {
    const { PostHog: PostHogClass } = await import('posthog-node');
    client = new PostHogClass(key, {
      host:
        process.env.POSTHOG_HOST ??
        process.env.VITE_POSTHOG_HOST ??
        'https://us.i.posthog.com',
      flushAt: 1,
      flushInterval: 0,
    });
    return client;
  } catch (err) {
    console.warn('[analytics] posthog-node unavailable, disabling capture', err);
    clientInitFailed = true;
    client = null;
    return null;
  }
}

interface CaptureArgs {
  distinctId: string;
  event: string;
  properties?: Record<string, unknown>;
  groups?: Record<string, string>;
}

// Cap how long we'll wait on flush() before returning. PostHog's queue
// will keep retrying in the background (the client is long-lived in our
// Hono API server) but we never want analytics to dominate response time
// for Stripe webhooks (which Stripe retries on slow responses) or for
// any synchronous handler. On Vercel Functions this also prevents the
// 5xx-on-cold-start case where ingest is briefly unreachable.
const FLUSH_TIMEOUT_MS = 1500;

// All work — including lazy client init — runs inside the try so a thrown
// constructor never escapes to callers. Analytics is best-effort: business
// logic must never fail because PostHog is misconfigured, unreachable, or
// slow.
export async function captureServerEvent(args: CaptureArgs): Promise<void> {
  try {
    const c = await getClient();
    if (!c) return;
    c.capture(args);
    // Race flush against a timeout. If the timer wins, the queued event
    // remains in posthog-node's internal buffer and will retry on next
    // flush — we just don't hold the response open waiting for the
    // network round-trip.
    await Promise.race([
      c.flush(),
      new Promise<void>((resolve) => setTimeout(resolve, FLUSH_TIMEOUT_MS)),
    ]);
  } catch (err) {
    console.warn('[analytics] capture failed', { event: args.event, err });
  }
}
